import * as React from "react";
import { useContext, useEffect } from "react";
import { GridDataStateChangeEvent, GridToolbar } from "@progress/kendo-react-grid";
import { DataResult, State } from "@progress/kendo-data-query";
import styled from "styled-components";
import { toast } from "react-toastify";
import _ from "lodash";
import { useTheme } from "@mui/material";
import StyledKendoGrid from "../../../../../Grid/StyledKendoGrid";
import LtlTabTableColumns from "./LtlTabTableColumns";
import clearRouting from "../ClearRouting";
import { EditProjectContext, EditProjectContextProps } from "../../../EditProjectContext";
import { LtlPipelineViewModel } from "../../../../ViewModel/LtlPipelineViewModel";
import { AddActiveRoutingButton } from "./AddActiveRoutingButton";
import { LtlRoutingViewModel } from "../../../../ViewModel/LtlRoutingViewModel";
import { ClearRoutingButton } from "./ClearRoutingButton";
import CommandColumn from "../../../../../Grid/ControlColumn/CommandColumn";
import { CommandDeleteRowButton } from "../../../../../Grid/ControlColumn/CommandDeleteRowButton";
import commandDeleteRow from "../../../../../Grid/ControlColumn/CommandDeleteRow";
import isLastItemInList from "./isLastItemInList";
import InlineEditProps from "../../../../../Grid/InlineEdit/InlineEditProps";
import fixLtlRoutingIdInLtlPipelines from "../../../FixLtlRoutingIdInLtlPipelines";
import { AddButton } from "./AddButton";
import { isFinished } from "./IsFinished";
import { isProjectReadOnly } from "../../../IsProjectReadOnly";
import { getSupplierTransitTime } from "../GetSupplierTransitTime";
import { ReadOnlyProps } from "../../../ReadOnlyProps";

const idField = "LtlRoutingId";

const setActiveRoutings = (
  { project, setProject }: EditProjectContextProps,
  spdrOrPdsrId: number | undefined,
  routing?: Array<LtlRoutingViewModel>
) => {
  const ltlPipelines = project?.ltlPipelines?.map((item: LtlPipelineViewModel) => {
    if (item.SpdrId !== spdrOrPdsrId && item.PdsrId !== spdrOrPdsrId) {
      return item;
    }

    const updatedItem: LtlPipelineViewModel = {
      ...item,
      routing,
      IsFinished: isFinished(item, routing),
      SupplierTransitTime: getSupplierTransitTime(routing, item.TTAdjustment).toString(),
    };
    return updatedItem;
  });
  // könnte eigentlich raus, fixLtlRoutingIdInLtlPipelines wird umgesetzt aber der state greift nicht
  setProject({
    ...project,
    ltlPipelines: fixLtlRoutingIdInLtlPipelines(ltlPipelines),
  });
};

type Props = {
  spdrOrPdsrId: number | undefined;
  ltlPipeline: LtlPipelineViewModel | undefined;
  unrouted: boolean;
};

export default function LtlTabTable({
  spdrOrPdsrId,
  ltlPipeline,
  unrouted,
  isReadOnly,
}: Props & ReadOnlyProps) {
  const editProjectContext = useContext(EditProjectContext);
  const { project } = useContext(EditProjectContext);

  const initialState: State = {
    take: 10,
    skip: 0,
  };

  const [dataState, setDataState] = React.useState<State>(initialState);
  const [dataResult, setDataResult] = React.useState<DataResult>({
    data: [],
    total: 0,
  });

  useEffect(() => {
    const data = ltlPipeline?.routing || [];
    setDataResult({
      data,
      total: data.length,
    });
  }, [ltlPipeline?.routing]);

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const onDataReceived = (newDataResult: DataResult) => {
    setDataResult(newDataResult);
    setActiveRoutings(
      editProjectContext,
      ltlPipeline?.SpdrId ?? ltlPipeline?.PdsrId,
      newDataResult.data
    );
  };

  const [editState, setEditState] = React.useState<number>();
  const inlineEditProps = InlineEditProps({
    idField,
    dataResult,
    setEditState,
    onDataReceived,
    isReadonly: isProjectReadOnly(project),
  });

  const commandColumn = CommandColumn({
    // eslint-disable-next-line react/no-unstable-nested-components
    commands: (dataItem: any) => [
      <CommandDeleteRowButton
        disabled={isReadOnly}
        key="CommandRemoveButton"
        onClick={() => {
          const isLastItem = isLastItemInList(dataResult.data, dataItem, idField);
          if (!isLastItem) {
            toast.error("You can only delete the last routing item!");
            return;
          }

          commandDeleteRow({
            idField,
            dataResult,
            setDataResult: onDataReceived,
            dataItem,
          });
        }}
      />,
    ],
  });

  const addNew = () => {
    const newDataItem = {
      LtlRoutingId: generateLtlRoutingId(dataResult.data as Array<LtlRoutingViewModel>),
    };

    setDataResult({
      data: [...dataResult.data, newDataItem],
      total: dataResult.total + 1,
    });

    setEditState(_.get(newDataItem, idField));
  };

  const generateLtlRoutingId = (routings: Array<LtlRoutingViewModel>) => {
    const max: number = _.max(routings.map((x) => x.LtlRoutingId || 0)) || 0;
    return max + 1;
  };

  const theme = useTheme();
  return (
    <StyledDiv>
      <StyledKendoGrid
        muiTheme={theme}
        resizable
        data={{
          data: dataResult.data.withEditfield(idField, editState),
          total: dataResult.total,
        }}
        {...dataState}
        onDataStateChange={onDataStateChange}
        {...inlineEditProps}
      >
        <GridToolbar>
          <ToolbarButtonWrapper>
            <AddButton onClick={addNew} disabled={isReadOnly} />
            <AddActiveRoutingButton
              ltlPipeline={ltlPipeline}
              onDataReceived={onDataReceived}
              disabled={unrouted || isReadOnly}
            />
            <ClearRoutingButton
              onClick={() => clearRouting(editProjectContext, spdrOrPdsrId)}
              ltlPipeline={ltlPipeline}
              disabled={unrouted || isReadOnly}
            />
          </ToolbarButtonWrapper>
        </GridToolbar>
        {[...LtlTabTableColumns({ isReadOnly }), commandColumn]}
      </StyledKendoGrid>
    </StyledDiv>
  );
}

const ToolbarButtonWrapper = styled.div`
  top: 0;
`;

const StyledDiv = styled.div`
  margin-bottom: 1rem;
`;
